/**
 * First-person walk (ADR 369) — the maths, kept out of the r3f component so it
 * is testable without a canvas.
 *
 * Frame: three.js, y up. The floor is the x/z plane; the twin's warehouse (X, Z)
 * lands on (x, z) here (ADR 25). `yaw` is the rotation about y with 0 looking
 * down −z, which is where a fresh PerspectiveCamera looks; `pitch` is the
 * rotation about the walker's right axis, positive = looking up. Both in
 * radians. Applied as `camera.rotation.set(pitch, yaw, 0, "YXZ")` — yaw first,
 * then pitch, so the horizon never rolls.
 */
import * as THREE from "three";
import { resolveMove, type BlockerField } from "./blockerField";

export interface WalkPose { x: number; y: number; z: number; yaw: number; pitch: number }

export interface WalkKeys {
  forward: boolean; back: boolean; left: boolean; right: boolean;
  up: boolean; down: boolean; run: boolean;
  /** Held: pass through racks and walls (ADR 370). Release and you are solid again. */
  ghost: boolean;
}

export const NO_KEYS: WalkKeys = {
  forward: false, back: false, left: false, right: false, up: false, down: false, run: false, ghost: false,
};

/** Eye height of a person standing on the floor. */
export const EYE_HEIGHT_M = 1.7;
/** Q/E and the wheel never go below standing height — a walk, not a crawl. */
export const MIN_EYE_M = EYE_HEIGHT_M;
/** Ceiling when the warehouse has no structure to measure; with racks, the
 *  ceiling is the top of the racking (`maxHeightM`). */
export const MAX_EYE_M = 12;
/** A brisk walk; Shift multiplies it — the webinar site is 480 m across. */
export const WALK_SPEED_MPS = 2.5;
export const RUN_MULTIPLIER = 4;
export const CLIMB_SPEED_MPS = 2;
/** Radians per pixel of mouse travel. */
export const LOOK_SENSITIVITY = 0.0025;
/** Straight up/down would flip the YXZ frame; stop just short. */
export const MAX_PITCH = Math.PI / 2 - 0.08;
/** Where the orbit target lands when walking ends: this far ahead, on the floor. */
export const EXIT_LOOK_AHEAD_M = 12;
export const WALK_FOV = 70;

export const KEY_BINDINGS: Record<string, keyof WalkKeys> = {
  KeyW: "forward", ArrowUp: "forward",
  KeyS: "back", ArrowDown: "back",
  KeyA: "left", ArrowLeft: "left",
  KeyD: "right", ArrowRight: "right",
  KeyE: "up", Space: "up",
  KeyQ: "down", KeyC: "down",
  ShiftLeft: "run", ShiftRight: "run",
  // X, not Alt: Alt+D is the address-bar shortcut in Chrome and Firefox, and D
  // is strafe-right — the first slide through a rack would steal the page's focus.
  KeyX: "ghost",
};

export function clampHeight(y: number, maxHeightM: number | null = null): number {
  const top = Math.max(MIN_EYE_M, maxHeightM ?? MAX_EYE_M);
  return Math.min(top, Math.max(MIN_EYE_M, y));
}

/** Mouse look. dx/dy are pixels; dragging right turns right, dragging up looks up. */
export function look(pose: WalkPose, dx: number, dy: number, sensitivity = LOOK_SENSITIVITY): WalkPose {
  const yaw = pose.yaw - dx * sensitivity;
  const pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, pose.pitch - dy * sensitivity));
  return { ...pose, yaw, pitch };
}

/** Forward on the floor for a yaw: (−sin, 0, −cos), the −z axis rotated about y. */
export function forwardOf(yaw: number): [number, number] {
  return [-Math.sin(yaw), -Math.cos(yaw)];
}

/** One tick of movement. Frame-rate independent: `dt` in seconds. Movement is
 *  on the floor plane regardless of pitch — looking up does not lift you. */
export function step(pose: WalkPose, keys: WalkKeys, dt: number, maxHeightM: number | null = null): WalkPose {
  const [fx, fz] = forwardOf(pose.yaw);
  const rx = -fz, rz = fx;                              // right = forward rotated −90° about y
  let ax = 0, az = 0;
  if (keys.forward) { ax += fx; az += fz; }
  if (keys.back)    { ax -= fx; az -= fz; }
  if (keys.right)   { ax += rx; az += rz; }
  if (keys.left)    { ax -= rx; az -= rz; }
  const len = Math.hypot(ax, az);
  const speed = WALK_SPEED_MPS * (keys.run ? RUN_MULTIPLIER : 1) * dt;
  const x = len > 0 ? pose.x + (ax / len) * speed : pose.x;
  const z = len > 0 ? pose.z + (az / len) * speed : pose.z;
  let y = pose.y;
  if (keys.up)   y += CLIMB_SPEED_MPS * dt;
  if (keys.down) y -= CLIMB_SPEED_MPS * dt;
  return { ...pose, x, z, y: clampHeight(y, maxHeightM) };
}

/**
 * A step, then the blockers' verdict on it (ADR 370): with a field and the
 * ghost key up, the floor move is resolved against the rack cells — slide
 * along a face, stop at a corner, never trapped. Height is never blocked; the
 * mask is 2-D.
 */
export function advance(pose: WalkPose, keys: WalkKeys, dt: number, field: BlockerField | null,
                        maxHeightM: number | null = null): WalkPose {
  const next = step(pose, keys, dt, maxHeightM);
  if (!field || keys.ghost) return next;
  const [x, z] = resolveMove(field, pose.x, pose.z, next.x, next.z);
  return { ...next, x, z };
}

/** Wheel: one notch is half a metre of eye height. */
export function wheel(pose: WalkPose, deltaY: number, maxHeightM: number | null = null): WalkPose {
  return { ...pose, y: clampHeight(pose.y - Math.sign(deltaY) * 0.5, maxHeightM) };
}

/**
 * Where walking starts: standing on the floor at the orbit target, eye height,
 * facing the way the orbit camera was looking (its horizontal direction). If
 * the camera sits directly above the target (top view) there is no horizontal
 * direction; face −z, the frame's default.
 */
export function enterPose(cameraPosition: THREE.Vector3, target: THREE.Vector3): WalkPose {
  const dx = target.x - cameraPosition.x;
  const dz = target.z - cameraPosition.z;
  const yaw = Math.hypot(dx, dz) < 1e-6 ? 0 : Math.atan2(-dx, -dz);
  return { x: target.x, y: EYE_HEIGHT_M, z: target.z, yaw, pitch: 0 };
}

/** Where orbiting resumes: camera at the walker's eye, target ahead on the floor. */
export function exitPose(pose: WalkPose): { position: THREE.Vector3; target: THREE.Vector3 } {
  const [fx, fz] = forwardOf(pose.yaw);
  return {
    position: new THREE.Vector3(pose.x, pose.y, pose.z),
    target: new THREE.Vector3(pose.x + fx * EXIT_LOOK_AHEAD_M, 0, pose.z + fz * EXIT_LOOK_AHEAD_M),
  };
}

export function applyPose(camera: THREE.Camera, pose: WalkPose): void {
  camera.position.set(pose.x, pose.y, pose.z);
  camera.rotation.set(pose.pitch, pose.yaw, 0, "YXZ");
}

/** Typing in a text field must not walk the camera. */
export function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return false;
  const tag = el.tagName.toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable === true;
}
